import { useState, type ReactNode } from 'react';
import { ActionBar } from '@flintmint/web-shared/components/action-bar';
import { AlertMessage } from '@flintmint/web-shared/components/alert';
import { Divider } from '@flintmint/web-shared/components/divider';
import { MenuItem } from '@flintmint/web-shared/components/menu-item';
import { Popover } from '@flintmint/web-shared/components/popover';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import type { FlowWithId } from '@/services/flows-service';
import { BulkWriteError } from '@/services/firestore-crud-service';
import {
  addFlowToModules,
  removeFlowFromModules,
  setModulesEnabled,
  type MedicalModuleWithId,
} from '@/services/medical-modules-service';

import styles from './bulk-action-bar.module.scss';

interface BulkActionBarProps {
  /** The selected modules, including ones the current filters hide. */
  modules: MedicalModuleWithId[];
  flows: FlowWithId[];
  onClear: () => void;
  onDeselect: (ids: string[]) => void;
}

export const BulkActionBar = ({ modules, flows, onClear, onDeselect }: BulkActionBarProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { showToastNotification } = useToaster();
  const { enable, disable, addToFlow, removeFromFlow } = bulkActions(modules, flows);

  const run = async (action: BulkAction) => {
    // Rows ticked while the write is in flight were not part of it, so they stay selected.
    const actedOnIds = modules.map((module) => module.id);
    setIsSaving(true);
    try {
      await action.run(getAuthUser().email ?? '');
      showToastNotification({ type: 'success', title: action.done });
      onDeselect(actedOnIds);
    } catch (error) {
      // The selection is kept, so re-running the action writes only the modules the failure left unchanged.
      const written = error instanceof BulkWriteError ? error.written : 0;
      showToastNotification({
        type: 'error',
        title: `Failed to ${action.toDo(action.count - written)}`,
        ...(written > 0 && { description: `Already updated: ${countLabel(written)}. Run the action again for the rest.` }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ActionBar on={modules.length > 0} clearable onClear={onClear} appearance="dark" className={styles.bar}>
      <div className={styles.content} role="toolbar" aria-label="Bulk actions" data-testid="module-bulk-bar">
        <span data-testid="module-bulk-count">{modules.length} selected</span>
        <Divider size={24} />
        <SingleAction label="Enable" action={enable} disabled={isSaving} onConfirm={run} testId="module-bulk-enable" />
        <SingleAction label="Disable" action={disable} disabled={isSaving} onConfirm={run} testId="module-bulk-disable" />
        <Divider size={24} />
        <FlowAction label="Add to flow" options={addToFlow} disabled={isSaving} onConfirm={run} testId="module-bulk-add-flow" />
        <FlowAction
          label="Remove from flow"
          options={removeFromFlow}
          disabled={isSaving}
          onConfirm={run}
          testId="module-bulk-remove-flow"
        />
      </div>
    </ActionBar>
  );
};

type BulkAction = {
  title: string;
  confirmLabel: string;
  danger?: boolean;
  /** Completes "Are you sure you want to …?" and "Failed to …" for `count` modules: `disable 2 modules`. */
  toDo: (count: number) => string;
  /** The success toast: `Disabled 2 modules`. */
  done: string;
  /** How many selected modules the action would change; the rest already match and are not written. */
  count: number;
  run: (userEmail: string) => Promise<void>;
};

type FlowOption = { flowId: string; flowName: string; action: BulkAction };

const bulkActions = (modules: MedicalModuleWithId[], flows: FlowWithId[]) => {
  const idsWhere = (predicate: (module: MedicalModuleWithId) => boolean) => modules.filter(predicate).map((module) => module.id);
  const hasFlow = (module: MedicalModuleWithId, flowId: string) => (module.flowIds ?? []).includes(flowId);

  const toEnable = idsWhere((module) => module.enabled === false);
  const toDisable = idsWhere((module) => module.enabled !== false);

  const enable: BulkAction = {
    title: 'Enable Modules',
    confirmLabel: 'Enable',
    toDo: (count) => `enable ${countLabel(count)}`,
    done: `Enabled ${countLabel(toEnable.length)}`,
    count: toEnable.length,
    run: (userEmail) => setModulesEnabled(toEnable, true, userEmail),
  };

  const disable: BulkAction = {
    title: 'Disable Modules',
    confirmLabel: 'Disable',
    toDo: (count) => `disable ${countLabel(count)}`,
    done: `Disabled ${countLabel(toDisable.length)}`,
    count: toDisable.length,
    run: (userEmail) => setModulesEnabled(toDisable, false, userEmail),
  };

  const addToFlow = flows.map((flow): FlowOption => {
    const ids = idsWhere((module) => !hasFlow(module, flow.id));
    return {
      flowId: flow.id,
      flowName: flow.name,
      action: {
        title: 'Add To Flow',
        confirmLabel: 'Add',
        toDo: (count) => `add ${countLabel(count)} to "${flow.name}"`,
        done: `Added ${countLabel(ids.length)} to "${flow.name}"`,
        count: ids.length,
        run: (userEmail) => addFlowToModules(ids, flow.id, userEmail),
      },
    };
  });

  const removeFromFlow = flows.map((flow): FlowOption => {
    const ids = idsWhere((module) => hasFlow(module, flow.id));
    return {
      flowId: flow.id,
      flowName: flow.name,
      action: {
        title: 'Remove From Flow',
        confirmLabel: 'Remove',
        danger: true,
        toDo: (count) => `remove ${countLabel(count)} from "${flow.name}"`,
        done: `Removed ${countLabel(ids.length)} from "${flow.name}"`,
        count: ids.length,
        run: (userEmail) => removeFlowFromModules(ids, flow.id, userEmail),
      },
    };
  });

  return { enable, disable, addToFlow, removeFromFlow };
};

const countLabel = (count: number) => `${count} module${count === 1 ? '' : 's'}`;

interface SingleActionProps {
  label: string;
  action: BulkAction;
  disabled: boolean;
  onConfirm: (action: BulkAction) => void;
  testId: string;
}

const SingleAction = ({ label, action, disabled, onConfirm, testId }: SingleActionProps) => (
  <ActionPopover label={label} disabled={disabled || action.count === 0} testId={testId}>
    {(close) => (
      <Confirmation
        action={action}
        cancelLabel="Cancel"
        onCancel={close}
        onConfirm={() => {
          close();
          onConfirm(action);
        }}
      />
    )}
  </ActionPopover>
);

interface FlowActionProps {
  label: string;
  options: FlowOption[];
  disabled: boolean;
  onConfirm: (action: BulkAction) => void;
  testId: string;
}

const FlowAction = ({ label, options, disabled, onConfirm, testId }: FlowActionProps) => {
  const [pickedFlowId, setPickedFlowId] = useState<string | null>(null);

  const changingOptions = options.filter((option) => option.action.count > 0);
  // Looked up on every render, so the confirmation always reflects the current selection.
  const picked = changingOptions.find((option) => option.flowId === pickedFlowId);

  return (
    <ActionPopover
      label={label}
      disabled={disabled || changingOptions.length === 0}
      testId={testId}
      onClose={() => setPickedFlowId(null)}
    >
      {(close) =>
        picked ? (
          <Confirmation
            action={picked.action}
            cancelLabel="Back"
            onCancel={() => setPickedFlowId(null)}
            onConfirm={() => {
              close();
              onConfirm(picked.action);
            }}
          />
        ) : (
          <div className={styles.menu}>
            <p className={styles.menuCaption}>Choose a flow</p>
            {changingOptions.map((option) => (
              <MenuItem
                key={option.flowId}
                name={option.flowName}
                rightNode={<span className={styles.menuCount}>{countLabel(option.action.count)}</span>}
                onClick={() => setPickedFlowId(option.flowId)}
                data-testid={`${testId}-option`}
                data-flow-name={option.flowName}
              />
            ))}
          </div>
        )
      }
    </ActionPopover>
  );
};

interface ActionPopoverProps {
  label: string;
  disabled: boolean;
  testId: string;
  onClose?: () => void;
  children: (close: () => void) => ReactNode;
}

const ActionPopover = ({ label, disabled, testId, onClose, children }: ActionPopoverProps) => {
  if (disabled) {
    return (
      <button type="button" disabled data-testid={testId}>
        {label}
      </button>
    );
  }

  return (
    <Popover
      arrow
      placement="top"
      offset={[0, 16]}
      maxWidth={290}
      cornerRadius="rounded"
      onClose={onClose}
      content={({ close }) => <>{children(close)}</>}
    >
      <button type="button" data-testid={testId}>
        {label}
      </button>
    </Popover>
  );
};

interface ConfirmationProps {
  action: BulkAction;
  cancelLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const Confirmation = ({ action, cancelLabel, onCancel, onConfirm }: ConfirmationProps) => (
  <AlertMessage
    type={action.danger ? 'danger' : 'warning'}
    icon={action.danger ? 'delete-01-bulk' : 'alert-01-bulk'}
    title={<>{action.title}</>}
    message={`Are you sure you want to ${action.toDo(action.count)}?`}
    primaryButton={{ children: action.confirmLabel, color: action.danger ? 'danger' : 'dark', onClick: onConfirm }}
    secondaryButton={{ children: cancelLabel, appearance: 'clear', onClick: onCancel }}
  />
);
